import * as yup from 'yup'
import {
  EMAIL_REGEX,
  PAGE_STORE_ORDERS,
  PASSWORD_REGEX,
  ROLES,
} from 'utils/constants'
import { PAGE_ACCESS_KEY_PREFIX } from 'components/userManagement/utils'

const PASSWORD_MESSAGE =
  'Minimum 8 characters, at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character.'

// schema for login page
export const loginSchema = yup
  .object()
  .shape({
    email: yup
      .string()
      .required('Email is required field')
      .matches(EMAIL_REGEX, 'Please Enter a valid email.'),
    password: yup.string().required('Password is required field'),
  })
  .required()

// schema for  forgot password
export const forgotPasswordSchema = yup
  .object()
  .shape({
    email: yup
      .string()
      .required('Email is required field')
      .matches(EMAIL_REGEX, 'Please Enter a valid email.'),
  })
  .required()

export const createPasswordSchema = yup.object().shape({
  password: yup.string().matches(PASSWORD_REGEX, PASSWORD_MESSAGE).required(),
  cpassword: yup
    .string()
    .required('Confirm Password is required field')
    .oneOf([yup.ref('password'), ''], 'Both Password must be same'),
})

export const addUserSchema = yup
  .object()
  .shape({
    firstName: yup.string().trim().required('First Name is required field'),
    // A Fresh Staff user signs in to the Fresh mobile app, which has no
    // approval-manager OTP step, so no approver applies to them.
    approverId: yup
      .string()
      .trim()
      .when('role', ([role], schema) =>
        role === ROLES.FRESH_STAFF
          ? schema.optional()
          : schema.required('Approver Manager is required field'),
      ),
    lastName: yup.string().trim().required('Last Name is required field'),
    empId: yup
      .string()
      .matches(
        /^[A-Z0-9]*$/,
        'Only uppercase alphanumeric characters are allowed',
      )
      .required('Employee ID is required field'),
    email: yup
      .string()
      .trim()
      .matches(EMAIL_REGEX, 'Please Enter a valid email.')
      .required('Email is required field'),
    password: yup.string().matches(PASSWORD_REGEX, PASSWORD_MESSAGE).required(),
    isActive: yup.boolean(),
    store: yup
      .string()
      .typeError('Invalid Store')
      .test('non-null', 'Store is Required', (value) => {
        return value !== 'NO_STORES'
      })
      .when(
        [PAGE_ACCESS_KEY_PREFIX + PAGE_STORE_ORDERS.id, 'role'],
        ([storeOrderPage, role], schema) => {
          // A Fresh stocker always works one store - the mobile app resets
          // that store's inventory, so the account is useless without it.
          if (role === ROLES.FRESH_STAFF) {
            return schema.required('Store is required for a Fresh Staff user')
          }

          if (typeof storeOrderPage === 'boolean')
            return schema.required(
              'Store is required when Store Order is selected in Page Access',
            )

          return schema
        },
      ),
    role: yup.string(),
    pinId: yup.string(),
    pickerName: yup.string().trim().optional(),
    externalPickerId: yup
      .string()
      .trim()
      .when('role', ([role], schema) =>
        role === ROLES.PICKER
          ? schema.required('Picker ID is required for picker role')
          : schema.optional(),
      ),
    permissions: yup.array().of(yup.string()).optional().default([]),
    // Required for a Fresh Staff user: the mobile app shows only the
    // categories they hold, so an account without any signs in to an empty
    // screen. For an Admin it is optional - but holding at least one is also
    // what grants them Fresh mobile access.
    freshCategories: yup
      .array()
      .of(yup.string())
      .default([])
      .when('role', ([role], schema) =>
        role === ROLES.FRESH_STAFF
          ? schema.min(1, 'Select at least one Fresh category')
          : schema,
      ),
  })
  .required()

// Will use the same schema as AddUser overiding the password field
export const updateUserSchema = addUserSchema.shape({
  password: yup
    .string()
    .optional()
    .trim()
    .test('isValidPassword', PASSWORD_MESSAGE, (value) => {
      // Allow null or validate against the regex
      return value === '' || PASSWORD_REGEX.test(value as string)
    }),
  empId: yup.string().optional(),
  email: yup.string().optional(),
})

export type UpdateUserType = yup.InferType<typeof updateUserSchema>
export type AddUserType = yup.InferType<typeof addUserSchema>
